/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СПРАВОЧНИК ПИКТОГРАММ текущего мероприятия
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ **ЭТО НЕ ПРАВА ДОСТУПА.** Пиктограмма — картинка на бейдже: обед,
 * автобус, парковка. Ни одна проверка прав в этом приложении не имеет права
 * смотреть сюда: за права отвечают `auth.isAdmin` (система) и
 * `event.canManage` (мероприятие).
 *
 * ⚠️ **ЧЕТВЁРТЫЙ НЕЗАВИСИМЫЙ СПРАВОЧНИК.** Зона — «куда пускают», уровень —
 * «насколько глубоко», пиктограмма — «что положено». Роль держит три набора
 * сразу и независимо.
 *
 * Справочник локален для мероприятия и пагинации не имеет: это десятки записей.
 *
 * ⚠️ Порядок в `items` — `(sortOrder, name)`, как его отдал сервер.
 * Пересортировывать на клиенте нельзя: `sortOrder` НЕ уникален, и сортировка
 * только по нему перетасовывала бы записи с одинаковой позицией на каждой
 * перерисовке.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  КЭШ КАРТИНОК
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `iconUrls` — `data:`-URL по КЛЮЧУ записи. Ключ содержит свежий uuid на
 * каждую загрузку (сервер выдаёт новое имя при каждой замене), поэтому
 * заменённая иконка не может показаться старой и инвалидация кэшу не нужна.
 *
 * `data:`, а не `URL.createObjectURL`: blob-URL надо освобождать руками, и
 * забытый `revokeObjectURL` — это утечка на экране, который открывают на весь
 * рабочий день. Плата — треть размера в памяти, что для иконки в 512 px
 * несущественно.
 *
 * Кэшируется только УСПЕХ: запомненный отказ означал бы, что после разрыва
 * сети картинка не появится до перезагрузки вкладки.
 *
 * `iconAspects` — пропорция (ширина / высота) той же картинки по тому же
 * ключу. Нужна бейджу: ячейка ряда пиктограмм ограничена по высоте, а ширину
 * берёт от картинки, и раскладка синхронна — ждать декодирования ей некогда.
 * Поэтому пропорция считается при загрузке байтов и кладётся ВМЕСТЕ с URL:
 * сначала пропорция, потом картинка, иначе первая отрисовка поставила бы
 * широкую иконку в квадрат, а вторая раздвинула бы ряд.
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { pictogramsApi } from '@/api/pictograms'
import type { ApiError } from '@/lib/api-error'
import { normalizeError } from '@/lib/api-error'
import type { CreatePictogramRequest, Pictogram, PictogramIcon } from '@/types/api'

export const usePictogramsStore = defineStore('pictograms', () => {
  /* ───────────────────────── state ───────────────────────── */

  const eventId = ref<string | null>(null)

  /** Отсортирован сервером: `(sortOrder, name)`. */
  const items = ref<Pictogram[]>([])

  const loading = ref(false)
  const error = shallowRef<ApiError | null>(null)
  const ready = ref(false)

  /** `data:`-URL иконок по ключу записи. Только успешные ответы. */
  const iconUrls = ref<Map<string, string>>(new Map())
  /** Ширина / высота иконки по ключу записи. Нет записи — считать квадратом. */
  const iconAspects = ref<Map<string, number>>(new Map())

  function rememberAspect(key: string, aspect: number | null): void {
    if (aspect === null) return
    const next = new Map(iconAspects.value)
    next.set(key, aspect)
    iconAspects.value = next
  }

  /* ───────────────────────── getters ─────────────────────── */

  const isEmpty = computed(() => items.value.length === 0)

  const byId = computed(() => new Map(items.value.map((pictogram) => [pictogram.id, pictogram])))

  /**
   * Пиктограмма по id, или `null`.
   *
   * `null` для пустого id и для id, которого в справочнике нет: запись могли
   * удалить, пока экран был открыт. Падать здесь нечему.
   */
  function get(pictogramId: string): Pictogram | null {
    if (!pictogramId) return null
    return byId.value.get(pictogramId) ?? null
  }

  /* ───────────────────────── запрос ──────────────────────── */

  let controller: AbortController | null = null

  async function load(id: string, force = false): Promise<void> {
    if (!force && eventId.value === id && ready.value && error.value === null) return

    controller?.abort()
    const ctl = new AbortController()
    controller = ctl

    if (eventId.value !== id) {
      items.value = []
      ready.value = false
      // Картинки чужого мероприятия здесь ни при чём, и держать их значило бы
      // тратить память на то, что уже не показать.
      iconUrls.value = new Map()
    iconAspects.value = new Map()
    }

    eventId.value = id
    loading.value = true
    error.value = null

    try {
      const data = await pictogramsApi.list(id, ctl.signal)
      if (controller !== ctl) return
      items.value = data.pictograms

      // ⚠️ Картинки тянем ЗДЕСЬ, а не в каждом компоненте, который их
      // показывает. Экранов два (справочник и панель галочек под ролью), и
      // асинхронный запрос из компонента означал бы, что каждый из них сам
      // борется с дедупликацией, отменой и порядком — а `iconUrls`
      // реактивен, так что шаблону достаточно из него читать.
      //
      // Без `await`: список обязан появиться сразу, а картинки —
      // догрузиться. Ошибки `iconUrl` глотает сам («рисуй без картинки»), так
      // что терять здесь нечего.
      for (const pictogram of data.pictograms) {
        if (pictogram.iconKey) void iconUrl(pictogram.iconKey)
      }
    } catch (err) {
      if (controller !== ctl) return

      const apiError = normalizeError(err)
      if (apiError.isCanceled) return

      error.value = apiError
      items.value = []
    } finally {
      if (controller === ctl) {
        controller = null
        loading.value = false
        ready.value = true
      }
    }
  }

  /* ───────────────────────── картинки ────────────────────── */

  /** Идущие запросы за байтами — дедупликация, см. ниже. */
  const inFlight = new Map<string, Promise<string | null>>()

  /**
   * `data:`-URL иконки по ключу записи, либо `null`.
   *
   * `null` — это «рисуй запись без картинки», а не ошибка: запись без иконки
   * законна, а ссылка на объект может пережить сам объект. Оба случая должны
   * выглядеть одинаково, и ни один экран не обязан знать, что попытка была.
   *
   * ⚠️ Промис кладётся в карту ДО `await`: справочник и панель галочек под
   * ролью монтируются в одном тике и обе видят пустой кэш — без дедупликации
   * одни и те же байты запрашивались бы дважды на каждую запись.
   */
  async function iconUrl(key: string): Promise<string | null> {
    const trimmed = key.trim()
    if (!trimmed) return null

    const cached = iconUrls.value.get(trimmed)
    if (cached) return cached

    const started = inFlight.get(trimmed)
    if (started) return started

    const id = eventId.value
    if (!id) return null

    const pending = pictogramsApi
      .fetchIcon(id, trimmed)
      .then(async (blob) => {
        const url = await blobToDataUrl(blob, blob.type)
        rememberAspect(trimmed, await imageAspect(blob))
        const next = new Map(iconUrls.value)
        next.set(trimmed, url)
        iconUrls.value = next
        return url
      })
      .catch(() => null)
      .finally(() => {
        inFlight.delete(trimmed)
      })

    inFlight.set(trimmed, pending)
    return pending
  }

  /* ───────────────────────── мутации ─────────────────────── */

  /** Бросают `ApiError`; вызывающий заворачивает в `useAsyncAction`. */

  /**
   * Загрузить иконку и получить её ключ.
   *
   * ⚠️ Записи это ещё не касается: ключ надо отправить в `create`/`update`.
   * Байты кладутся в кэш сразу — они уже в руках, и читать их обратно с
   * сервера ради превью означало бы round trip за тем, что лежит в переменной.
   */
  async function uploadIcon(file: File): Promise<PictogramIcon> {
    const id = requireEventId()
    const icon = await pictogramsApi.uploadIcon(id, file)

    // Пропорция — из ответа сервера: он пересобрал картинку и знает, что
    // получилось. Декодировать ради неё выбранный файл незачем.
    rememberAspect(icon.key, icon.width > 0 && icon.height > 0 ? icon.width / icon.height : null)
    const next = new Map(iconUrls.value)
    next.set(icon.key, await blobToDataUrl(file, icon.contentType))
    iconUrls.value = next

    return icon
  }

  async function create(input: CreatePictogramRequest): Promise<Pictogram> {
    const id = requireEventId()
    const response = await pictogramsApi.create(id, input)
    // Перечитываем, а не дописываем в конец: сортировка серверная, и
    // вставленная руками запись с позицией 5 встала бы после позиции 20.
    await load(id, true)
    return response.pictogram
  }

  async function update(pictogramId: string, input: CreatePictogramRequest): Promise<Pictogram> {
    const id = requireEventId()
    const response = await pictogramsApi.update(id, pictogramId, input)
    await load(id, true)
    return response.pictogram
  }

  /**
   * ⚠️ Привязки к ролям уходят каскадом НА СЕРВЕРЕ, и этот стор о них ничего не
   * знает. После удаления вызывающий обязан перечитать справочник ролей —
   * иначе роли продолжат держать id несуществующей пиктограммы.
   */
  async function remove(pictogramId: string): Promise<Pictogram> {
    const id = requireEventId()
    const response = await pictogramsApi.remove(id, pictogramId)
    await load(id, true)
    return response.pictogram
  }

  function requireEventId(): string {
    const id = eventId.value
    if (!id) throw new Error('[pictograms] мутация вне скоупа мероприятия')
    return id
  }

  /* ───────────────────────── $reset ──────────────────────── */

  function $reset(): void {
    controller?.abort()
    controller = null
    inFlight.clear()

    eventId.value = null
    items.value = []
    loading.value = false
    error.value = null
    ready.value = false
    iconUrls.value = new Map()
    iconAspects.value = new Map()
  }

  return {
    // state
    eventId,
    items,
    loading,
    error,
    ready,
    iconUrls,
    iconAspects,
    // getters
    isEmpty,
    byId,
    get,
    // actions
    load,
    iconUrl,
    uploadIcon,
    create,
    update,
    remove,
    $reset,
  }
})

/**
 * Блоб → `data:`-URL.
 *
 * `contentType` берётся у сервера, а не у блоба: у ответа axios с
 * `responseType: 'blob'` тип обычно есть, но у только что выбранного в
 * `<input type="file">` файла он приходит от ОС и бывает пустым.
 */
async function blobToDataUrl(blob: Blob, contentType: string): Promise<string> {
  const typed = contentType && blob.type !== contentType
    ? new Blob([await blob.arrayBuffer()], { type: contentType })
    : blob

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('не удалось прочитать иконку'))
    reader.readAsDataURL(typed)
  })
}

/**
 * Пропорция картинки (ширина / высота), либо `null` — не удалось узнать.
 *
 * `null`, а не 1: неизвестная пропорция и квадратная — разные вещи, и бейдж
 * сам решает, чем заменить незнание (квадратом, как печаталось раньше).
 */
async function imageAspect(blob: Blob): Promise<number | null> {
  if (typeof createImageBitmap !== 'function') return null
  try {
    const bitmap = await createImageBitmap(blob)
    const aspect = bitmap.width > 0 && bitmap.height > 0 ? bitmap.width / bitmap.height : null
    bitmap.close()
    return aspect
  } catch {
    return null
  }
}
